import { useEffect, useState, type ReactNode } from 'react';
import PredicateQuestionLoop from './PredicateQuestionLoop';
import { getEntityQuestions, answerEntityQuestion } from '../../lib/entities/entityQuestions';
import type { OnboardingQuestion } from '../../lib/entities/onboarding';

/**
 * Completeness gate for an entity's predicates.
 *
 * Capture happens create-then-ask, so an entity can end up with an applicable predicate left unanswered:
 * the browser was closed mid-capture, the network dropped between answers, the row predates enforcement,
 * or a new asked predicate was added to the catalog after the entity was created. An unanswered asked
 * predicate is absent, and the resolver reads absent as FALSE, so it silently shows the wrong tier.
 *
 * So on entity load we ask the server for the next unanswered applicable question. If there is one, this
 * renders a NON-DISMISSABLE modal running the same mandatory yes/no loop as capture, and does not go away
 * until every applicable predicate has an answer. Then it calls `onCompleted` so the caller can
 * re-resolve. When nothing is outstanding it renders nothing. See COMPLIANCE-MODEL.md §5, §14.
 */
export default function EntityQuestionGate({
  base,
  id,
  lead,
  onCompleted,
}: {
  /** The entity collection path, e.g. "/api/employees" or "/api/cases". */
  base: string;
  id: string;
  lead: ReactNode;
  /** Called once the last outstanding question is answered, so the caller can re-resolve. */
  onCompleted: () => void;
}) {
  const [question, setQuestion] = useState<OnboardingQuestion | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let alive = true;
    setChecked(false);
    setQuestion(null);
    getEntityQuestions(base, id)
      .then((next) => {
        if (!alive) return;
        setQuestion(!next.done && next.question ? next.question : null);
        setChecked(true);
      })
      .catch(() => {
        // A failed check must not block the page; the edit surfaces still enforce completeness on save.
        if (alive) setChecked(true);
      });
    return () => {
      alive = false;
    };
  }, [base, id]);

  if (!checked || !question) return null;

  return (
    // No onClick on the scrim and no close control: these answers are required, not dismissable.
    <div className="modal-scrim" role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Required questions"
        onClick={(e) => e.stopPropagation()}
      >
        <PredicateQuestionLoop
          lead={lead}
          initialQuestion={question}
          submitAnswer={(key, value) => answerEntityQuestion(base, id, key, value)}
          onDone={() => {
            setQuestion(null);
            onCompleted();
          }}
        />
      </div>
    </div>
  );
}
